import { useEffect, useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAnalytics } from '../context/AnalyticsContext';
import { trackEvents, trackPageView, trackStickerDesign } from '../utils/analytics';

interface OrderDetails {
  orderId: string;
  orderName?: string;
  customerDetails: {
    name: string;
    email: string;
    phone?: string;
  };
  orderDetails: {
    quantity: number;
    size: string;
    shape: string;
    finish: string;
    price: number;
  };
  paymentDetails?: {
    method: string;
  };
  shippingDetails?: {
    method: string;
    estimatedDelivery: string;
  };
  imageUrl?: string;
}

export default function Success() {
  const [searchParams] = useSearchParams();
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const sessionId = searchParams.get('session_id');
  const { analytics } = useAnalytics();

  // Track page view
  useEffect(() => {
    trackPageView(analytics, 'Order Success', {
      session_id: sessionId || undefined
    });
  }, [analytics, sessionId]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!sessionId) return;
      
      try {
        const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/order-details?session_id=${sessionId}`);
        if (!response.ok) throw new Error('Failed to fetch order details');
        
        const data = await response.json();
        setOrderDetails(data);
        
        // Track checkout completed event
        if (data) {
          trackStickerDesign(analytics, trackEvents.CHECKOUT_COMPLETED, {
            order_id: data.orderId,
            order_name: data.orderName || undefined,
            customer_email: data.customerDetails?.email,
            customer_name: data.customerDetails?.name || undefined,
            customer_phone: data.customerDetails?.phone || undefined,
            quantity: data.orderDetails?.quantity,
            shape: data.orderDetails?.shape,
            size: data.orderDetails?.size,
            finish: data.orderDetails?.finish,
            price: data.orderDetails?.price,
            payment_method: data.paymentDetails?.method || undefined,
            shipping_method: data.shippingDetails?.method || undefined,
            estimated_delivery: data.shippingDetails?.estimatedDelivery || undefined,
            session_id: sessionId,
            completion_time: new Date().toISOString(),
            order_status: 'confirmed'
          });
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [sessionId, analytics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="mt-2 text-lg font-medium text-gray-900">Order details not found</h3>
            <p className="mt-1 text-sm text-gray-500">
              We couldn't find your order details. Please contact customer support.
            </p>
            <div className="mt-6">
              <a
                href="/"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Return to Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-md max-w-md w-full p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h1 className="mt-3 text-lg font-medium text-gray-900">Order Successful!</h1>
          <p className="mt-2 text-sm text-gray-500">
            Thank you for your order. We've received your payment and will begin processing your stickers.
          </p>
        </div>
        
        <div className="mt-6">
          <h2 className="text-sm font-medium text-gray-900">Order Details</h2>
          <div className="mt-2 border-t border-b border-gray-200 py-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Order ID:</span>
              <span className="font-medium">{orderDetails.orderId}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Quantity:</span>
              <span className="font-medium">{orderDetails.orderDetails.quantity} stickers</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Size:</span>
              <span className="font-medium">{orderDetails.orderDetails.size}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Shape:</span>
              <span className="font-medium">{orderDetails.orderDetails.shape}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Finish:</span>
              <span className="font-medium">{orderDetails.orderDetails.finish}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total:</span>
              <span className="font-medium">${orderDetails.orderDetails.price.toFixed(2)}</span>
            </div>
          </div>
        </div>
        
        {orderDetails.imageUrl && (
          <div className="mt-6">
            <h2 className="text-sm font-medium text-gray-900">Your Design</h2>
            <div className="mt-2 border border-gray-200 rounded-md overflow-hidden">
              <img 
                src={orderDetails.imageUrl} 
                alt="Your sticker design" 
                className="w-full h-auto"
              />
            </div>
          </div>
        )}
        
        <div className="mt-6">
          <h2 className="text-sm font-medium text-gray-900">What's Next?</h2>
          <p className="mt-2 text-sm text-gray-500">
            We'll send a confirmation email to {orderDetails.customerDetails.email} with your order details.
            Your stickers will be produced and shipped within 3-5 business days.
          </p>
        </div>
        
        <div className="mt-6">
          <a
            href="/"
            className="block w-full text-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Create Another Design
          </a>
        </div>
      </div>
    </div>
  );
}
